// @ts-nocheck

import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import { debounce, get } from 'lodash'
import { Component, Suspense } from 'react'
import {
  getNewResidentHandoverNotes,
  getNominative
} from '../../../common/utils'
import Field from '../../fields'
import { Icon } from '../../generic'
import Form from '..'

const DATE_FORMAT = 'DD-MM-YYYY'
const FORM_ID = 'completeInitialAssessment'
const WAIT_TIME = 500

interface InitialAssessmentCompleteFormProps {
  features: Record<string, any>
  fields: Record<string, any>
  resident: Record<string, any>
  preferredPeopleNominative: Record<string, any>
  setFormField: (field: string, value: any) => void
}

interface InitialAssessmentCompleteFormState {
  hasManuallyChangedNotes: boolean
}

class InitialAssessmentCompleteForm extends Component<
  InitialAssessmentCompleteFormProps,
  InitialAssessmentCompleteFormState
> {
  state: InitialAssessmentCompleteFormState = {
    hasManuallyChangedNotes: false
  }

  componentDidMount() {
    const { resident } = this.props
    if (resident) {
      this.setAdmissionDate()
      this.setHandoverNotes({ resident })
    }
  }

  componentDidUpdate(prevProps: InitialAssessmentCompleteFormProps) {
    const { fields, resident } = this.props
    const { hasManuallyChangedNotes } = this.state

    if (!prevProps.resident && resident) {
      this.setAdmissionDate()
      this.setHandoverNotes({ resident })
    }

    if (prevProps.fields && fields && !hasManuallyChangedNotes) {
      if (this.hasChangedField(prevProps.fields, fields, 'admissionDate')) {
        this.updateNotesWithNewDate(resident, fields.admissionDate)
      }
      if (this.hasChangedField(prevProps.fields, fields, 'contactsInformed')) {
        this.setHandoverNotes({
          resident,
          contactsInformed: fields.contactsInformed
        })
      }
      if (
        fields.flagForHandover &&
        this.hasChangedField(prevProps.fields, fields, 'handoverNotes')
      ) {
        this.setState({ hasManuallyChangedNotes: true })
      }
    }
  }

  hasChangedField = (
    prevFields: Record<string, any>,
    fields: Record<string, any>,
    fieldId: string
  ) => {
    return (
      (fields[fieldId] || fields[fieldId] === false) &&
      prevFields[fieldId] !== fields[fieldId]
    )
  }

  updateNotesWithNewDate = debounce(
    (resident: Record<string, any>, date: string) => {
      const newDate = dayjs(date, DATE_FORMAT)
      const shouldChangeNotes = newDate && newDate.isValid()
      if (resident && shouldChangeNotes) {
        this.setHandoverNotes({ resident, newDate })
      }
    },
    WAIT_TIME
  )

  setAdmissionDate = () => {
    const { resident, setFormField } = this.props
    if (resident.admissionDate) {
      setFormField({
        key: 'admissionDate',
        value: dayjs(resident.admissionDate).format(DATE_FORMAT)
      })
    }
  }

  setHandoverNotes = ({
    resident,
    newDate,
    contactsInformed
  }: Record<string, any>) => {
    const { fields, setFormField } = this.props
    if (fields) {
      const chosenDate = fields.admissionDate || newDate
      setFormField({
        key: 'handoverNotes',
        value: getNewResidentHandoverNotes({
          resident,
          overrideDate: chosenDate && dayjs(chosenDate, DATE_FORMAT),
          contactsInformed: contactsInformed || fields.contactsInformed
        })
      })
    }
  }

  renderAdmissionDateField = () => (
    <div
      style={{
        ...styles.field,
        ...styles.admissionDateField
      }}
    >
      <span style={styles.admissionDateLabel}>Admission date</span>
      <div style={styles.dateContainer}>
        <div style={styles.dateIcon}>
          <Icon fill="black" name="calendar" size={24} />
        </div>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys="admissionDate"
            label="Admission date"
            layout="no-label"
            formId={FORM_ID}
            options={{
              date: true,
              delimiter: '-',
              datePattern: ['d', 'm', 'Y']
            }}
            placeholder={DATE_FORMAT}
            type="formattedText"
            validationTypes="validDate"
            values={this.props.fields}
          />
        </Suspense>
      </div>
    </div>
  )

  renderHandoverNotes = () => {
    const { fields } = this.props
    const hasFlagged = !!get(fields, 'flagForHandover')
    const handoverNotes = get(fields, 'handoverNotes')

    return hasFlagged ? (
      <Suspense fallback={<div>Loading...</div>}>
        <Field
          className="lmc-sub-card-edit"
          formId={FORM_ID}
          isNestedField
          keys="handoverNotes"
          label="Handover notes for all staff"
          layout="column"
          type="textarea"
          values={fields}
        />
      </Suspense>
    ) : (
      <div>
        <span style={styles.disabledInputLabel}>
          Handover notes for all staff
        </span>
        <div className="lmc-sub-card" style={styles.disabledInput}>
          {handoverNotes || ''}
        </div>
      </div>
    )
  }

  render() {
    const { features, fields, resident, preferredPeopleNominative } = this.props
    if (!features || !fields || !resident) return null
    return (
      <div className="lmc-card" style={styles.wrapper}>
        <div style={styles.fieldsContainer}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={FORM_ID}
              keys="residentAccepted"
              label={`${getNominative(
                preferredPeopleNominative,
                'Resident'
              )} accepted`}
              required
              type="singleCheckbox"
              values={fields}
            />
          </Suspense>
          {this.renderAdmissionDateField()}
          <div style={styles.field}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={FORM_ID}
                keys="contactsInformed"
                label="Family / social worker informed"
                type="singleCheckbox"
                values={fields}
              />
            </Suspense>
          </div>
          {features?.features?.handovers?.enabled && (
            <div style={styles.field}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  formId={FORM_ID}
                  leftIcon="flag"
                  keys="flagForHandover"
                  label="Flag for handover?"
                  type="singleCheckbox"
                  values={fields}
                />
              </Suspense>
            </div>
          )}
        </div>
        {features?.features?.handovers?.enabled && (
          <div style={styles.field}>{this.renderHandoverNotes()}</div>
        )}
      </div>
    )
  }
}

const styles = {
  admissionDateField: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row wrap'
  },
  admissionDateLabel: {
    fontWeight: 'bold',
    marginRight: 12,
    userSelect: 'none'
  },
  dateContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1
  },
  dateIcon: {
    marginRight: 18
  },
  disabledInput: {
    color: colors.darkgrey,
    minHeight: 130
  },
  disabledInputLabel: {
    display: 'block',
    marginBottom: 5,
    userSelect: 'none'
  },
  field: {
    marginTop: 24
  },
  fieldsContainer: {
    margin: '0 auto',
    maxWidth: 350
  },
  wrapper: {
    padding: 24
  }
}

export default Form(InitialAssessmentCompleteForm, FORM_ID)
